"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { UIMessage } from "ai";
import { ArrowDownIcon, DownloadIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ComponentProps, Key, ReactNode, Ref } from "react";
import {
  Fragment,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Virtualizer,
  type CustomContainerComponentProps,
  type VirtualizerHandle,
  type VirtualizerProps,
} from "virtua";

/**
 * Conversation — virtualized, auto-scrolling chat message list.
 *
 * Backed by `virtua` (replacing `react-virtuoso`). The Virtualizer instance handles:
 *   • zero-config dynamic-size virtualization (ResizeObserver-based)
 *   • scroll-position preservation when older messages are prepended (`shift`)
 *   • imperative scroll-to-index via `VirtualizerHandle`
 *
 * Unlike react-virtuoso, virtua has no built-in `followOutput` policy.
 * `useConversationScroll` therefore owns only the follow/not-follow decision;
 * Virtua remains the sole owner of item measurement and prepend positioning.
 *
 * This component is the library adapter; `useConversationScroll` is the policy
 * controller. Data, streaming, and HITL code stay outside both layers.
 */

export type ConversationHandle = VirtualizerHandle;

export type ConversationProps<T = unknown> = Omit<
  VirtualizerProps<T>,
  "style" | "className" | "ref" | "as"
> & {
  className?: string;
  /** Signals a late DOM-only size change such as Mermaid rendering. */
  onContentResize?: () => void;
  /** Signals a composer, keyboard, or other viewport-size change. */
  onViewportResize?: () => void;
  /** Called before a direct wheel, touch, or scrollbar scroll gesture. */
  onUserScroll?: () => void;
  /** Called for a trusted pointer or scroll interaction in the transcript. */
  onUserInteraction?: (event: Event) => void;
  /**
   * Called when the reader explicitly gestures toward older content.
   * Provisional Virtua scroll offsets are not user intent, so the conversation
   * list uses this signal to leave stick-to-bottom mode without measurement
   * corrections accidentally disabling follow.
   */
  onUserScrollAway?: () => void;
  /**
   * Stable identity for render-prop items. Virtua uses the returned element's
   * React key for its internal ListItem key, falling back to the index when
   * no key is present.
   */
  getItemKey?: (item: T, index: number) => Key;
  /** Render-prop for each item (same signature as Virtualizer's children). */
  children: ReactNode | ((data: T, index: number) => ReactNode);
};

export const Conversation = forwardRef(VirtualizerInternal) as <T>(
  props: ConversationProps<T> & { ref?: Ref<ConversationHandle> }
) => ReturnType<typeof VirtualizerInternal>;

function setRef<T>(ref: Ref<T> | null | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    (ref as { current: T | null }).current = value;
  }
}

const ContentResizeContext = createContext<(() => void) | undefined>(undefined);

const ObservedVirtualContent = forwardRef<
  HTMLDivElement,
  CustomContainerComponentProps
>(({ style, children }, forwardedRef) => {
  const onContentResize = useContext(ContentResizeContext);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = contentRef.current;
    if (!element || !onContentResize || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(onContentResize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [onContentResize]);

  const assignRef = useCallback((element: HTMLDivElement | null) => {
    contentRef.current = element;
    setRef(forwardedRef, element);
  }, [forwardedRef]);

  return (
    <div ref={assignRef} style={style}>
      {children}
    </div>
  );
});
ObservedVirtualContent.displayName = "ObservedVirtualContent";

function VirtualizerInternal<T = unknown>(
  {
    className,
    children,
    onContentResize,
    onViewportResize,
    onUserScroll,
    onUserInteraction,
    onUserScrollAway,
    getItemKey,
    ...props
  }: ConversationProps<T>,
  ref: Ref<ConversationHandle>
) {
  const lastTouchYRef = useRef<number | null>(null);
  const draggingScrollbarRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const virtualizerChildren = useMemo<VirtualizerProps<T>["children"]>(() => {
    if (!getItemKey || typeof children !== "function") {
      return children as VirtualizerProps<T>["children"];
    }

    // Virtua 0.49 keys its internal ListItem from the element returned by the
    // render prop. Put the caller's key on that boundary rather than relying
    // on an implementation detail of the row it happens to return.
    return (item: T, index: number) => (
      <Fragment key={getItemKey(item, index)}>
        {children(item, index)}
      </Fragment>
    );
  }, [children, getItemKey]);

  useEffect(() => {
    const element = scrollContainerRef.current;
    if (!element || !onViewportResize || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(onViewportResize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [onViewportResize]);

  return (
    <div
      ref={scrollContainerRef}
      className={cn(
        "relative min-h-0 flex flex-1 flex-col overflow-y-auto [overflow-anchor:none]",
        className,
      )}
      onPointerDown={(event) => {
        onUserInteraction?.(event.nativeEvent);
        // Native scrollbar pointer events target the scroll container. Include
        // a small overlay-scrollbar gutter for platforms whose scrollbar does
        // not consume layout width.
        const scrollbarGutter = Math.max(
          event.currentTarget.offsetWidth - event.currentTarget.clientWidth,
          16,
        );
        const rightEdge = event.currentTarget.getBoundingClientRect().right;
        if (
          event.target === event.currentTarget
          && event.clientX >= rightEdge - scrollbarGutter
        ) {
          draggingScrollbarRef.current = true;
          onUserScroll?.();
          onUserScrollAway?.();
        }
      }}
      onPointerMove={() => {
        if (draggingScrollbarRef.current) onUserScroll?.();
      }}
      onPointerUp={() => {
        draggingScrollbarRef.current = false;
      }}
      onPointerCancel={() => {
        draggingScrollbarRef.current = false;
      }}
      onPointerLeave={() => {
        draggingScrollbarRef.current = false;
      }}
      onTouchEnd={() => {
        lastTouchYRef.current = null;
      }}
      onTouchMove={(event) => {
        onUserInteraction?.(event.nativeEvent);
        const currentY = event.touches[0]?.clientY;
        const previousY = lastTouchYRef.current;
        if (
          currentY !== undefined
          && previousY !== null
        ) {
          if (currentY !== previousY) onUserScroll?.();
          if (currentY > previousY) onUserScrollAway?.();
        }
        lastTouchYRef.current = currentY ?? null;
      }}
      onTouchStart={(event) => {
        lastTouchYRef.current = event.touches[0]?.clientY ?? null;
      }}
      onWheel={(event) => {
        onUserInteraction?.(event.nativeEvent);
        if (event.deltaY !== 0) onUserScroll?.();
        if (event.deltaY < 0) onUserScrollAway?.();
      }}
      onKeyDown={(event) => {
        onUserInteraction?.(event.nativeEvent);
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        const target = event.target as Element;
        if (
          target !== event.currentTarget
          && target.closest(
            "input, textarea, select, button, a[href], [contenteditable]:not([contenteditable='false'])",
          )
        ) {
          return;
        }
        const scrollsAway = (
          event.key === "ArrowUp"
          || event.key === "PageUp"
          || event.key === "Home"
          || (event.key === " " && event.shiftKey)
        );
        const scrollsTowardTail = (
          event.key === "ArrowDown"
          || event.key === "PageDown"
          || event.key === "End"
          || (event.key === " " && !event.shiftKey)
        );
        if (scrollsAway || scrollsTowardTail) onUserScroll?.();
        if (scrollsAway) onUserScrollAway?.();
      }}
      role="log"
    >
      {/* Official Virtua chat pattern: this is real flex layout, not estimated
          unloaded history. It shrinks to zero for scrollable conversations. */}
      <div aria-hidden="true" className="flex-grow" />
      <ContentResizeContext.Provider value={onContentResize}>
        <Virtualizer<T> {...props} as={ObservedVirtualContent} ref={ref}>
          {virtualizerChildren}
        </Virtualizer>
      </ContentResizeContext.Provider>
    </div>
  );
}

/**
 * ConversationScrollButton — floating "scroll to bottom" arrow.
 *
 * Shown only when the viewport is not pinned to the bottom AND the list has
 * content to scroll to. The scroll controller owns `isAtBottom` and exposes a
 * tail-follow command through `onScrollToBottom`; this presentation component
 * never receives the virtualizer handle.
 *
 * The `hasMessages` guard is important: when the list is empty (e.g. the
 * empty-state placeholder is being rendered), the viewport can briefly report
 * `isAtBottom=false` — and showing a "scroll to bottom" arrow that would
 * scroll to an empty list is nonsensical.  Hide the button until there is at
 * least one message.
 */
export type ConversationScrollButtonProps = ComponentProps<typeof Button> & {
  isAtBottom: boolean;
  onScrollToBottom: () => void;
  /**
   * Whether the conversation has at least one message. When false (empty
   * thread / loading the initial fetch), the button is hidden even if
   * `isAtBottom` is false.  Defaults to true so existing callers that
   * don't pass it behave unchanged.
   */
  hasMessages?: boolean;
};

export const ConversationScrollButton = ({
  className,
  isAtBottom,
  onScrollToBottom,
  hasMessages = true,
  ...props
}: ConversationScrollButtonProps) => {
  const { t } = useTranslation("chat");
  const handleScrollToBottom = useCallback(() => {
    onScrollToBottom();
  }, [onScrollToBottom]);

  return (
    (!isAtBottom && hasMessages) && (
      <Button
        className={cn(
          "absolute bottom-4 left-[50%] translate-x-[-50%] rounded-full dark:bg-background dark:hover:bg-muted",
          className
        )}
        aria-label={t("navigation.continueScrolling")}
        onClick={handleScrollToBottom}
        size="icon"
        type="button"
        variant="outline"
        {...props}
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    )
  );
};

export type ConversationEmptyStateProps = ComponentProps<"div"> & {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
};

export const ConversationEmptyState = ({
  className,
  title,
  description,
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => {
  const { t } = useTranslation("chat");
  const resolvedTitle = title ?? t("conversationEmpty.title");
  const resolvedDescription = description ?? t("conversationEmpty.description");
  return <div
    className={cn(
      "flex size-full flex-col items-center justify-center gap-3 p-8 text-center",
      className
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon && <div className="text-muted-foreground">{icon}</div>}
        <div className="space-y-1">
          <h3 className="font-medium text-sm">{resolvedTitle}</h3>
          {resolvedDescription && (
            <p className="text-muted-foreground text-sm">{resolvedDescription}</p>
          )}
        </div>
      </>
    )}
  </div>;
};

const getMessageText = (message: UIMessage): string =>
  message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("");

export type ConversationDownloadProps = Omit<
  ComponentProps<typeof Button>,
  "onClick"
> & {
  messages: UIMessage[];
  filename?: string;
  formatMessage?: (message: UIMessage, index: number) => string;
};

const defaultFormatMessage = (message: UIMessage): string => {
  const roleLabel =
    message.role.charAt(0).toUpperCase() + message.role.slice(1);
  return `**${roleLabel}:** ${getMessageText(message)}`;
};

export const messagesToMarkdown = (
  messages: UIMessage[],
  formatMessage: (
    message: UIMessage,
    index: number
  ) => string = defaultFormatMessage
): string => messages.map((msg, i) => formatMessage(msg, i)).join("\n\n");

export const ConversationDownload = ({
  messages,
  filename = "conversation.md",
  formatMessage = defaultFormatMessage,
  className,
  children,
  ...props
}: ConversationDownloadProps) => {
  const handleDownload = useCallback(() => {
    const markdown = messagesToMarkdown(messages, formatMessage);
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [messages, filename, formatMessage]);

  return (
    <Button
      className={cn(
        "absolute top-4 right-4 rounded-full dark:bg-background dark:hover:bg-muted",
        className
      )}
      onClick={handleDownload}
      size="icon"
      type="button"
      variant="outline"
      {...props}
    >
      {children ?? <DownloadIcon className="size-4" />}
    </Button>
  );
};
